import { Trans } from '@lingui/macro'
import { CloudDownload, Restore } from '@mui/icons-material'
import { Alert, Button, Dialog, DialogContent, Grid, IconButton, Tooltip, Typography } from '@mui/material'
import { datetimeFormat } from 'app/appSettings'
import { getFormBackups } from 'app/services/sfAuth/sfData/sfForms'
import { ButtonIcon } from 'app/views/common-components/ButtonIcon'
import { DialogTitleWithIconClose } from 'app/views/common-components/DialogTitleWithIconClose'
import { useFormikContext } from 'formik'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { FORM_EDITOR_DATA_KEYS, FORM_EDITOR_REDUX_KEYS } from './FormEditorUtils'
import { FormBackup } from '../FormTypes'

export const FormBackups: React.FC<{ backups: FormBackup[], formId: string }> = ({ backups = [], formId }) => {
  const [currentBackups, setCurrentBackups] = useState<FormBackup[]>(backups)
  const [showBackups, setShowBackups] = useState<boolean>(false)
  const [restoringBackup, setRestoringBackup] = useState<FormBackup | undefined>()

  const { setValues, values } = useFormikContext()
  const dispatch = useDispatch()

  useEffect(() => {
    getFormBackups(formId).then(backups => {
      setCurrentBackups(backups)
    }, reject => {
      console.error(reject)
    })
  }, [showBackups])

  const backupsToRender = currentBackups
    .sort((a, b) => moment(b.createdDate).unix() - moment(a.createdDate).unix())
    .filter((obj, index) => index < 20)

  return (
    <>
      <Dialog
        maxWidth='pu2'
        fullWidth
        open={showBackups}
      >
        <DialogTitleWithIconClose
          label={<Trans>FORM_BACKUPS</Trans>}
          handleClose={e => {
            setShowBackups(false)
          }}
        />
        {backupsToRender.length > 0 &&
          <Grid container gap='16px' sx={{ padding: '20px 24px' }}>
            <Grid item xs>
              <Typography sx={{ color: 'primary.main' }}>
                <Trans>CREATED_BY</Trans>
              </Typography>
            </Grid>
            <Grid item xs>
              <Typography sx={{ color: 'primary.main' }}>
                <Trans>CREATED_DATE</Trans>
              </Typography>
            </Grid>
            <Grid item xs>
              <Typography sx={{ color: 'primary.main' }}>
                <Trans>PORTAL_VERSION</Trans>
              </Typography>
            </Grid>
            <Grid item sx={{ width: '40px' }} />
          </Grid>}
        <DialogContent>
          {backupsToRender.length > 0
            ? backupsToRender
              .map((backup, index) => {
                return (
                  <Grid key={index} container gap='16px' sx={{ pb: '16px' }}>
                    <Grid item xs>
                      {backup.createdBy}
                    </Grid>
                    <Grid item xs>
                      {moment.utc(backup.createdDate).format(datetimeFormat)}
                    </Grid>
                    <Grid item xs>
                      {backup.portalVersion}
                    </Grid>
                    <Grid item>
                      <Tooltip title={<Trans>RESTORE_BACKUP</Trans>}>
                        <span><IconButton
                          onClick={e => {
                            setShowBackups(false)
                            setRestoringBackup(backup)
                          }}
                        >
                          <Restore />
                        </IconButton></span>
                      </Tooltip>
                    </Grid>
                  </Grid>
                )
              })
            : <Alert>
              <Trans>NO_BACKUPS_YET</Trans>
              </Alert>}
        </DialogContent>
      </Dialog>
      <Dialog maxWidth='xs' fullWidth open={Boolean(restoringBackup)}>
        <DialogTitleWithIconClose
          handleClose={e => {
            setRestoringBackup(undefined)
            setShowBackups(true)
          }}
        />
        <DialogContent>
          <Typography>
            <Trans>
              ARE_YOU_SURE_RESTORE_BACKUP_FROM
            </Trans>
            {` ${moment.utc(restoringBackup?.createdDate).format(datetimeFormat)}?`}
          </Typography>
          <Grid container direction='column' gap='16px' sx={{ mt: '16px' }}>
            <Button
              fullWidth
              variant='contained'
              color='primary'
              onClick={(e) => {
                dispatch({
                  type: 'SET_SECTIONS',
                  sections: restoringBackup?.data.sections
                })
                const toSet = { ...values }
                FORM_EDITOR_DATA_KEYS.forEach(key => {
                  if (!FORM_EDITOR_REDUX_KEYS.includes(key)) {
                    toSet[key] = restoringBackup?.data[key]
                  }
                })
                setValues(toSet)
                setRestoringBackup(undefined)
                setShowBackups(false)
              }}
            >
              <Trans>
                YES
              </Trans>
            </Button>
            <Button
              fullWidth
              variant='outlined'
              color='primary'
              onClick={e => {
                setRestoringBackup(undefined)
                setShowBackups(true)
              }}
            >
              <Trans>
                NO
              </Trans>
            </Button>
          </Grid>
        </DialogContent>

      </Dialog>
      <Button
        variant='contained'
        color='primary'
        onClick={e => {
          setShowBackups(true)
        }}
      >
        <Trans>FORM_BACKUPS</Trans>
        <ButtonIcon IconClass={CloudDownload} />
      </Button>
    </>
  )
}
